import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {OrdersVerificationDialogConfig} from "./orders-verification-dialog.config";
import {DetectMethodChanges, DetectSetterChanges, findHCF, isVoid} from "../../utils";
import {ComboHighlightedItem} from "../../ui-messages/ui-messages";
import {LastQuoteCacheService} from "../../last-quote-cache.service";
import {MessageBusService} from "../../message-bus.service";
import {Subscription} from "rxjs";
import {QuoteDto} from "../../shell-communication/dtos/quote-dto.class";
import {parseOptionTicker} from "../../options-common/options.model";
import {OrdersGroupToRegroup} from "../../orders-regroup-dialog/OrdersGroupToRegroup";
import {OrderToRegroupWrapper} from "../../orders-regroup-dialog/OrderToRegroupWrapper";
import {previewFromPlan} from "../../orders-regroup-dialog/arrangement-preview";
import {ArrangementPlanPages, planOptimalArrangement} from "../../orders-regroup-dialog/arrangement-planner";
import {OoaClientService} from "../../orders-regroup-dialog/ooa-client.service";
import {HedgeOrder} from "../../hedging-grid/data-model/hedge-order";
import {GroupMetadata, TransactionLeg} from "../../hedging-grid/pricing-steps-popup/pricing-steps-popup.component";

interface VerificationModel {
    hcf?: number;
    transPrice?: number;
    transMarketValue?: number;
    orderLegs?: ComboHighlightedItem[];
    ledgerPrice?: () => number;
    defaultQty: number;
    isNewHedge?: boolean;
    hedgeName?: string;
    hedgePlaceholder?: string;
    /** Preview panel only (CR-20260827-001): the position-pinned zone name - 'Main', 'Linked #1', … */
    title?: string;
    /** Preview panel only: this order carries the pile's naked leg(s) - flagged, execute first. */
    naked?: boolean;
    groupName?: string;
    groupColor?: string;
}

/**
 * Supplied by the Hedge Matrix so its groups can open the HG Pricing Steps popup from this
 * dialog. The dialog cannot host the popup itself: PricingStepsPopupComponent lives in
 * HedgingGridModule, which imports this dialog's module - embedding it here would be circular.
 */
export type PricingStepsHandler =
    (legs: TransactionLeg[], defaultQty: number, metadata: GroupMetadata) => void;


@Component({
    selector: 'ets-orders-verification-dialog',
    templateUrl: 'orders-verification-dialog.component.html',
    styleUrls: ['orders-verification-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class OrdersVerificationDialogComponent implements OnInit {

    constructor(
        private _changeDetector: ChangeDetectorRef,
        private readonly _lastQuoteCache: LastQuoteCacheService,
        private readonly _messageBus: MessageBusService,
        private readonly _ooaClient: OoaClientService
    ) {
    }

    private _resolve: (value?: (PromiseLike<unknown> | unknown)) => void;
    private _reject: (reason?: any) => void;
    private readonly _subscriptions: Subscription[] = [];
    private _configs: OrdersVerificationDialogConfig[] = [];
    existingLabels: string[] = [];

    verifications: VerificationModel[] = [];

    title = 'Order Verification Dialog';

    hcf: number = 1;

    transPrice: number;

    transMarketValue: number;

    // ---------------------------------------------------------------------------------------
    // EARLY PREVIEW OF THE OPTIMAL ARRANGEMENT (CR-20260827-001; backend-served since
    // CR-20260830-002)
    //
    // When a caller opts in (Hedge Matrix copy-orders and acquire paths only - the dialog is
    // also OPG's and the ML Pad's, and those stay untouched), the BACKEND arrangement runs
    // behind the blocking spinner as the dialog opens. A non-Unchanged reply shows the
    // "Optimize Grouping" button; clicking it widens the dialog and shows the arranged orders
    // in this dialog's own format, read-only, with a pager when two ranked solutions came
    // back. The preview carries NO buttons (owner: "absolutely no buttons - it's a pure
    // preview") and the confirm path still copies the ORIGINAL orders. A DEGRADED answer
    // (naked residue) renders RED instead of orange - the run is presented as failed.
    // ---------------------------------------------------------------------------------------

    private _offerOptimization = false;

    private _atm: number = null;

    /** Bumped on every show(); an awaited reply is applied only if its token is still current. */
    private _openToken = 0;

    arrangementPages: ArrangementPlanPages = null;

    previewPageIx = 0;

    get previewPageCount(): number {
        return this.arrangementPages && this.arrangementPages.status === 'ok'
            ? this.arrangementPages.pages.length
            : 0;
    }

    /** RED replaces orange when the shown page carries naked residue (owner ruling). */
    get previewDegraded(): boolean {
        if (this.previewPageCount === 0) {
            return false;
        }
        const page = this.arrangementPages.pages[Math.min(this.previewPageIx, this.previewPageCount - 1)];
        return page.degraded === true;
    }

    /** The shown page's naked-leg order name(s) ('Linked #2'), or '' when the page has none. */
    get previewNakedTitles(): string {
        return this.previewVerifications.filter(v => v.naked).map(v => v.title).join(', ');
    }

    /** Set only by Hedge Matrix callers; its presence is what shows the pricing-steps icons. */
    private _pricingStepsHandler: PricingStepsHandler;

    get pricingStepsAvailable(): boolean {
        return !isVoid(this._pricingStepsHandler);
    }

    /** Non-null once the improver found a better arrangement. Cleared on hide. */
    previewVerifications: VerificationModel[] = [];

    optimizeAvailable = false;

    previewVisible = false;

    /** The preview doubles the dialog: the second panel is "exactly of the same size" (owner). */
    get width(): number {
        return this.previewVisible ? 1220 : 600;
    }

    /**
     * Auto height while the dialog is the plain verification - exactly the pre-CR behaviour.
     * A FIXED height once the preview opens (CR-20260827-001): the two panes then have a real
     * height to fill, which is what lets each side's scroll view actually scroll instead of the
     * content being clipped at the popup's max-height, and what pins the buttons to the bottom.
     * (Replaced a row-count formula that had been dead since the template moved to height=auto.)
     */
    get height(): string {
        return this.previewVisible ? '90vh' : 'auto';
    }

    private _isLoading: boolean = false;
    get isLoading(): boolean {
        return this._isLoading;
    }

    @DetectSetterChanges()
    set isLoading(v: boolean) {
        this._isLoading = v;
    }

    /**
     * The blocking spinner's text. Starts as the stock "Loading...". CR-20260901-003: the text
     * is now driven by the backend's progress milestones - the engine reports what it ACTUALLY
     * holds (a full arrangement / proof that none exists / a partial one) and the spinner says
     * exactly that. The old blind 4s timer CLAIMED "some solutions are already found", which was
     * false whenever the search ran long and delivered nothing. The timer survives only as an
     * honest fallback for a search that has reported nothing yet.
     */
    loadingMessage: string = OrdersVerificationDialogComponent.DEFAULT_LOADING_MESSAGE;

    private static readonly DEFAULT_LOADING_MESSAGE = 'Loading...';

    private static readonly SLOW_ARRANGEMENT_MESSAGE =
        'Still searching for the optimal arrangement... Nothing found so far';

    private static readonly MILESTONE_MESSAGES: { [milestone: string]: string } = {
        searching: 'Searching for the optimal arrangement...',
        arrangementFound: 'An arrangement is found. Looking for a better one...',
        noFullClosure: 'A complete grouping is impossible for these orders. ' +
            'Searching for the best partial arrangement...',
        partialFound: 'A partial arrangement is found. Looking for a better one...'
    };

    /**
     * The waiting-time ceiling appended to every milestone message (owner ruling 2026-09-01):
     * without the cap the user cannot tell a long search from a hang. The seconds come from the
     * backend push - the engine's REAL budget for the current phase, never a frontend guess.
     */
    private static milestoneMessage(milestone: string, phaseBudgetSeconds: number): string | undefined {

        const base = OrdersVerificationDialogComponent.MILESTONE_MESSAGES[milestone];

        if (!base) {
            return undefined;
        }

        const seconds = Math.round(phaseBudgetSeconds);

        return seconds > 0 ? `${base} This phase takes up to ${seconds} seconds` : base;
    }

    private static readonly SLOW_ARRANGEMENT_NOTICE_MS = 4000;

    private _slowLoadTimer: ReturnType<typeof setTimeout> = null;

    private _progressSub: Subscription = null;

    /**
     * Swap the spinner text (owner-directed round 3, 2026-09-01): the message update runs
     * under @DetectMethodChanges - the codebase's OnPush idiom - instead of the rounds 1-2
     * imperative repaint/reposition plumbing, which never took.
     */
    @DetectMethodChanges({delay: 25})
    private setLoadingMessage(message: string): void {
        this.loadingMessage = message;
    }

    isVisible: boolean;

    /** Marker class on the popup's overlay wrapper - scopes the stretch-fix CSS to THIS popup. */
    readonly popupWrapperAttr = {class: 'ovd-popup'};

    context: string;

    get showCopyOrdersButton() {
        return this.context !== 'mlpad';
    }

    get confirmButtonText(): string {
        if (this.context === 'acquire') {
            return 'Got it! Acquire Changes';
        }
        return 'Got it! Copy Orders';
    }

    get canConfirm(): boolean {
        if (this.context !== 'acquire') {
            return true;
        }
        var newHedges = this.verifications.filter(function(v) { return v.isNewHedge; });
        if (newHedges.length === 0) {
            return true;
        }
        return newHedges.every(function(v) {
            var name = (v.hedgeName || '').trim();
            if (name.length === 0) { return false; }
            if (this.existingLabels.indexOf(name) >= 0) { return false; }
            var otherNewHedges = this.verifications.filter(function(o) { return o.isNewHedge && o !== v; });
            var duplicate = otherNewHedges.some(function(o) { return (o.hedgeName || '').trim() === name; });
            return !duplicate;
        }.bind(this));
    }

    @DetectMethodChanges()
    onHedgeNameChanged() {
        // Triggers change detection so canConfirm getter re-evaluates
    }

    ngOnInit() {
    }

    @DetectMethodChanges()
    show(configs: OrdersVerificationDialogConfig[], context?: string, existingLabels?: string[],
         offerOptimization?: boolean, pricingStepsHandler?: PricingStepsHandler,
         atm?: number): Promise<void> {

        this.context = context;
        this._configs = configs;
        this.existingLabels = existingLabels || [];
        this._offerOptimization = offerOptimization === true;
        this._pricingStepsHandler = pricingStepsHandler;
        this._atm = typeof atm === 'number' && isFinite(atm) ? atm : null;
        this._openToken++;

        const models = configs.map(cfg => {
            const orderLegs = cfg.orderLegs || [];

            orderLegs.forEach(leg => {
                this._lastQuoteCache.subscribeIfNotYet(leg.ticker);
                const lastQuote = this._lastQuoteCache.getLastQuote(leg.ticker);
                leg.price = lastQuote?.mid;
            });

            const hcf = findHCF(cfg.orderLegs.map(x => x.netPosition));

            const transPrice = cfg.orderPrice;

            const defaultQty = cfg.defaultQty;

            const model : VerificationModel = {
                orderLegs,
                hcf,
                transPrice,
                ledgerPrice: cfg.ledgerPrice,
                defaultQty,
                isNewHedge: cfg.isNewHedge,
                hedgeName: cfg.hedgeName || '',
                hedgePlaceholder: cfg.hedgePlaceholder || 'Enter hedge name',
                groupName: cfg.groupName,
                groupColor: cfg.groupColor,
            }

            this.updateTransMarketValue(cfg);

            return model;
        });

        this._subscriptions.push(
            this._messageBus.of<QuoteDto[]>('QuoteDto')
                .subscribe(msg => this.onQuote(msg.payload))
        );

        this.verifications = models;

        this.isVisible = true;

        // The dialog opens IMMEDIATELY and the spinner holds it while the backend arrangement
        // runs (owner: "show the dialog, but show the spinner on the dialog, which will block
        // the interaction naturally"). The call is genuinely async now - no paint-deferral
        // needed; the promise is deliberately not awaited so show() returns its own promise.
        if (this._offerOptimization) {
            this.isLoading = true;
            this.armSlowLoadNotice();
            this.computeOptimization();
        }

        return new Promise((resolve, reject) => {
            this._resolve = resolve;
            this._reject = reject;
        });
    }

    /**
     * Run the backend arrangement over the cards this dialog is showing and, when it answers
     * with something other than "Unchanged", prebuild the preview models for the best solution.
     * Failure means "no offer", never a broken dialog: the original verification flow is the
     * product, the preview is an extra.
     */
    private async computeOptimization(): Promise<void> {

        const token = this._openToken;

        try {

            const groups = this.buildPlannerGroups();

            const result = await planOptimalArrangement(groups, [], this._ooaClient, this._atm);

            if (token !== this._openToken || !this.isVisible) {
                // The dialog was closed (or reopened for a different package) while the request
                // was in flight - the late answer must not mutate the new state.
                return;
            }

            this.arrangementPages = result;
            this.previewPageIx = 0;

            if (result.status !== 'ok' || result.pages.length === 0) {
                this.optimizeAvailable = false;
                this.previewVerifications = [];

                if (result.status === 'error') {
                    console.error('[optimize-grouping] arrangement failed - no offer made');
                }

                return;
            }

            this.buildPreviewForPage(0);
            this.optimizeAvailable = true;

        } catch (e) {

            console.error('[optimize-grouping] preview computation failed - no offer made', e);

            if (token === this._openToken) {
                this.optimizeAvailable = false;
                this.previewVerifications = [];
            }

        } finally {
            if (token === this._openToken) {
                this.isLoading = false;
                this.disarmSlowLoadNotice();
            }
            this._changeDetector.detectChanges();
        }
    }

    /**
     * CR-20260901-003: milestone-driven spinner text. The backend pushes a progress event on
     * every engine milestone TRANSITION and this maps it straight to the message - the spinner
     * only ever claims what the engine actually reported. The 4-second timer stays as the honest
     * fallback ("still searching, nothing found so far") for a run that has not reported yet;
     * a real milestone supersedes and cancels it. The token guard makes a late timer or a late
     * event over a reopened dialog a no-op.
     */
    private armSlowLoadNotice(): void {

        this.disarmSlowLoadNotice();

        const token = this._openToken;

        this._progressSub = this._ooaClient.progress$.subscribe(progress => {

            if (token !== this._openToken || !this._isLoading) {
                return;
            }

            const message = OrdersVerificationDialogComponent.milestoneMessage(
                progress.milestone, progress.phaseBudgetSeconds);

            if (!message) {
                return;
            }

            if (this._slowLoadTimer !== null) {
                clearTimeout(this._slowLoadTimer);
                this._slowLoadTimer = null;
            }

            this.setLoadingMessage(message);
        });

        this._slowLoadTimer = setTimeout(() => {

            this._slowLoadTimer = null;

            if (token !== this._openToken || !this._isLoading) {
                return;
            }

            this.setLoadingMessage(OrdersVerificationDialogComponent.SLOW_ARRANGEMENT_MESSAGE);

        }, OrdersVerificationDialogComponent.SLOW_ARRANGEMENT_NOTICE_MS);
    }

    private disarmSlowLoadNotice(): void {

        if (this._slowLoadTimer !== null) {
            clearTimeout(this._slowLoadTimer);
            this._slowLoadTimer = null;
        }

        if (this._progressSub !== null) {
            this._progressSub.unsubscribe();
            this._progressSub = null;
        }

        this.loadingMessage = OrdersVerificationDialogComponent.DEFAULT_LOADING_MESSAGE;
    }

    /** The pager: rebuild the read-only preview for the selected ranked solution. */
    @DetectMethodChanges()
    onPreviewPageChanged(pageIx: number) {

        if (pageIx < 0 || pageIx >= this.previewPageCount) {
            return;
        }

        this.previewPageIx = pageIx;
        this.buildPreviewForPage(pageIx);
    }

    private buildPreviewForPage(pageIx: number): void {

        const preview = previewFromPlan(this.arrangementPages.pages[pageIx]);

        if (preview === null) {
            this.previewVerifications = [];
            return;
        }

        const displayNames = new Map<string, string>();
        this._configs.forEach(cfg => (cfg.orderLegs || [])
            .forEach(leg => displayNames.set(leg.ticker, leg.tickerDisplayName)));

        const defaultQty = this._configs[0]?.defaultQty;

        this.previewVerifications = preview.map(order => {

            const orderLegs = order.legs.map(leg => {

                const item: ComboHighlightedItem = {
                    ticker: leg.ticker,
                    tickerDisplayName: displayNames.get(leg.ticker) || leg.ticker,
                    underlying: parseOptionTicker(leg.ticker)?.underlying,
                    netPosition: Math.abs(leg.qty),
                    side: Math.sign(leg.qty),
                    itemType: null,
                    strategyId: null
                };

                this._lastQuoteCache.subscribeIfNotYet(leg.ticker);
                item.price = this._lastQuoteCache.getLastQuote(leg.ticker)?.mid;

                return item;
            });

            const model: VerificationModel = {
                title: order.title,
                naked: order.naked,
                orderLegs,
                hcf: order.hcf,
                defaultQty,
                // R9: the chunk-ledger formula - the same number the Re-group dialog's zone
                // would show. Live: it reads the legs' current prices on every call.
                ledgerPrice: () => this.chunkLedgerPrice(orderLegs, order.hcf, defaultQty),
            };

            this.updateTransPrice(model);

            return model;
        });
    }

    /**
     * One planner group per verification card, mirroring what the Re-group dialog is fed
     * downstream: the card's legs with SIGNED quantities. Everything the planner needs beyond
     * the `ComboHighlightedItem` (right, strike, expiration) is recovered from the ticker.
     */
    private buildPlannerGroups(): OrdersGroupToRegroup[] {

        return this._configs.map((cfg, ix) => {

            const wrappers = (cfg.orderLegs || [])
                .map(leg => {

                    const signedQty = (leg.netPosition || 0) * ((leg.side as number) || 0);

                    if (signedQty === 0) {
                        return null;
                    }

                    const parsed = parseOptionTicker(leg.ticker);

                    if (isVoid(parsed)) {
                        throw new Error(`not an option ticker: ${leg.ticker}`);
                    }

                    const order: HedgeOrder = {
                        ticker: leg.ticker,
                        action: signedQty > 0 ? 'Buy To Open' : 'Sell To Open',
                        qty: signedQty,
                        asset: parsed.underlying,
                        type: parsed.type,
                        expiration: parsed.expiration,
                        strike: parsed.strike,
                        price: leg.price
                    };

                    return new OrderToRegroupWrapper(order);
                })
                .filter(w => w !== null);

            return new OrdersGroupToRegroup(`Order #${ix + 1}` as any, wrappers);
        });
    }

    /**
     * The Re-group dialog's `getChunkLedgerPrice` arithmetic, over this dialog's leg shape:
     * per-unit market price of the chunk, scaled by how many units of the portfolio default
     * quantity the chunk represents.
     */
    private chunkLedgerPrice(orderLegs: ComboHighlightedItem[], hcf: number, defaultQty: number): number {

        const total = orderLegs
            .map(leg => Math.abs(leg.price) * (leg.netPosition * (leg.side as number)) * -1)
            .reduce((a, b) => a + b, 0);

        return (total / hcf) * (hcf / defaultQty);
    }

    @DetectMethodChanges()
    toggleOptimizePreview() {
        this.previewVisible = !this.previewVisible;
    }

    /**
     * Opens the HG Pricing Steps popup (via the caller-supplied handler) for ONE group of
     * legs - the group whose header icon was clicked, original or preview alike.
     */
    showPricingSteps(model: VerificationModel, index: number) {

        if (!this.pricingStepsAvailable) {
            return;
        }

        const legs: TransactionLeg[] = (model.orderLegs || [])
            .map(leg => ({
                ticker: leg.ticker,
                qty: (leg.netPosition || 0) * ((leg.side as number) || 0),
                priceAsOwned: false
            }))
            .filter(leg => leg.qty !== 0);

        if (legs.length === 0) {
            return;
        }

        const name = (model.isNewHedge ? (model.hedgeName || '').trim() : '')
            || model.groupName
            || model.title
            || `Order #${index + 1}`;

        const metadata: GroupMetadata = {
            name,
            color: model.groupColor || '',
            expiration: '',
            legType: 'transaction'
        };

        this._pricingStepsHandler(legs, model.defaultQty, metadata);
    }

    @DetectMethodChanges()
    onHidden() {
        this.isVisible = false;
        this._subscriptions.forEach(x => x.unsubscribe());
        this._subscriptions.length = 0;
        this._reject = null;
        this._resolve = null;
        this._offerOptimization = false;
        this._pricingStepsHandler = null;
        this.optimizeAvailable = false;
        this.previewVisible = false;
        this.previewVerifications = [];
        this.isLoading = false;
        this.disarmSlowLoadNotice();
        this.arrangementPages = null;
        this.previewPageIx = 0;
        this._atm = null;
        this._openToken++;
    }

    @DetectMethodChanges()
    onOkClicked() {
        // Write hedge names back to configs so caller can read them
        this.verifications.forEach(function(v, i) {
            if (v.isNewHedge && this._configs[i]) {
                this._configs[i].hedgeName = (v.hedgeName || '').trim();
            }
        }.bind(this));
        this._resolve();
        this.isVisible = false;
    }

    @DetectMethodChanges()
    onCancelClicked() {
        this._reject();
        this.isVisible = false;
    }

    private onQuote(payload: QuoteDto[]) {

        // The preview legs are the SAME contracts re-grouped, so they live-update on the same
        // stream; a preview showing stale prices next to a live original would read as a bug.
        this.verifications.concat(this.previewVerifications).forEach(vm => {
            let hasMatch = false;

            vm.orderLegs?.forEach(leg => {
                payload.forEach(q => {
                    if (q.ticker !== leg.ticker) {
                        return;
                    }
                    leg.price = q.mid;
                    hasMatch = true;
                });
            });
            if (!hasMatch) {
                return;
            }
            this.updateTransPrice(vm);
        });

        this._changeDetector.detectChanges();
    }

    @DetectMethodChanges()
    private updateTransPrice(model: VerificationModel) {
        const totalPx = model.orderLegs
            .map(leg => leg.netPosition * leg.side * -1 * leg.price)
            .reduce((a, b) => a + b, 0);

        const transPrice = totalPx / model.hcf;

        model.transPrice = transPrice;

        this.updateTransMarketValue(model);
    }

    @DetectMethodChanges()
    private updateTransMarketValue(model: VerificationModel) {
        model.transMarketValue = model.transPrice * model.hcf * 100;
    }
}